import React, { useState, useEffect, useCallback } from "react";

const BLUE = "#4F7FFA";
const CYAN = "#00BCD4";
const WEEKLY_REPORT_KEY = "elde_weekly_report_v3";
const NUDGE_KEY = "elde_last_nudge";
const NUDGE_COOLDOWN_HOURS = 24;
// Once the user has actually seen the weekly report, it stays visible for this
// long, then auto-hides — its numbers can go stale as the user logs new data.
const WEEKLY_VIEW_HOURS = 24;

// ─── Helpers ────────────────────────────────────────────────────────────────

function daysSince(isoDate) {
  if (!isoDate) return 999;
  return (Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24);
}

function hoursSince(isoDate) {
  if (!isoDate) return 999;
  return (Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60);
}

function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);
}

async function callClaude(prompt) {
  const isNative = !!(window.Capacitor?.isNativePlatform?.());
  const baseUrl = isNative ? "https://elde.ai" : "";
  const response = await fetch(`${baseUrl}/api/ellie`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt, max_tokens: 100 }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "API error");
  return data.text || "";
}

// ─── Nudge trigger detection ─────────────────────────────────────────────────

export function detectNudgeTrigger(accounts, allPayments, monthlyPayments, planSettings) {
  if (!accounts.length) return null;

  const totalDebt = accounts.reduce((s, a) => s + a.balance, 0);
  const totalMin = accounts.reduce((s, a) => s + a.minPayment, 0);
  const allPaid = accounts.filter(a => a.paymentDate).every(a => !!monthlyPayments?.paid?.[a.id]);
  const allPaymentsList = Object.values(allPayments).flat();
  const thisMonthPayments = allPaymentsList.filter(p => {
    const d = new Date(p.date + "T00:00:00");
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const thisMonthPrincipal = thisMonthPayments.reduce((s, p) => s + (p.principal || 0), 0);
  const thisMonthInterest = thisMonthPayments.reduce((s, p) => s + (p.interest || 0), 0);

  // Trigger 1: All payments marked paid this month
  if (allPaid && thisMonthPrincipal > 0) {
    return {
      type: "all_paid",
      data: { thisMonthPrincipal, thisMonthInterest, totalDebt, accounts },
    };
  }

  // Trigger 2: Account almost paid off (< 3 months remaining)
  for (const a of accounts) {
    if (a.balance <= 0) continue;
    const r = a.rate / 100 / 12;
    if (a.minPayment <= a.balance * r) continue;
    const months = Math.ceil(Math.log(a.minPayment / (a.minPayment - a.balance * r)) / Math.log(1 + r));
    if (months <= 3) {
      return { type: "almost_done", data: { account: a, months, totalDebt, accounts } };
    }
  }

  // Trigger 3: Extra budget set but small — suggest increasing
  if (planSettings.extraBudget > 0 && planSettings.extraBudget < totalMin * 0.1) {
    const focusAcct = accounts.sort((a, b) => b.rate - a.rate)[0];
    return { type: "small_extra", data: { extraBudget: planSettings.extraBudget, focusAcct, accounts } };
  }

  // Trigger 4: High interest account with no extra payments
  const highRateAcct = accounts.filter(a => a.rate >= 20).sort((a, b) => b.rate - a.rate)[0];
  if (highRateAcct && planSettings.extraBudget === 0) {
    return { type: "high_interest", data: { account: highRateAcct, accounts } };
  }

  return null;
}

function buildWeeklyPrompt(accounts, allPayments, monthlyPayments, planSettings) {
  const totalDebt = accounts.reduce((s, a) => s + a.balance, 0);
  const totalMin = accounts.reduce((s, a) => s + a.minPayment, 0);
  const allPaymentsList = Object.values(allPayments).flat();
  const recentPayments = allPaymentsList.filter(p => daysSince(p.date) <= 7);
  const weekPrincipal = recentPayments.reduce((s, p) => s + (p.principal || 0), 0);
  const weekInterest = recentPayments.reduce((s, p) => s + (p.interest || 0), 0);
  const paidCount = Object.keys(monthlyPayments?.paid || {}).length;

  const today = new Date();
  const accountSummary = accounts.map(a => {
    const isPaid = !!monthlyPayments?.paid?.[a.id];
    let dueStatus = "no due date set";
    if (a.paymentDate) {
      let dueDate;
      if (typeof a.paymentDate === "string" && a.paymentDate.includes("-")) {
        dueDate = new Date(a.paymentDate + "T00:00:00");
      } else {
        const day = parseInt(a.paymentDate);
        dueDate = new Date(today.getFullYear(), today.getMonth(), day);
        if (dueDate < today) dueDate = new Date(today.getFullYear(), today.getMonth() + 1, day);
      }
      const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      if (isPaid) dueStatus = "paid this month";
      else if (daysUntil === 0) dueStatus = "due today (not yet missed)";
      else if (daysUntil > 0) dueStatus = `due in ${daysUntil} day${daysUntil !== 1 ? "s" : ""}`;
      else dueStatus = `overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? "s" : ""}`;
    }
    return `- ${a.name}: $${a.balance.toFixed(2)} balance, ${a.rate}% APR, $${a.minPayment}/mo minimum, status: ${dueStatus}`;
  }).join("\n");

  return `You are Ellie, an encouraging but honest AI debt coach inside the ELDE app. Generate a brief weekly debt health report for this user.

User's debt snapshot:
${accountSummary}

Total debt: ${formatCurrency(totalDebt)}
Total monthly minimums: ${formatCurrency(totalMin)}
Extra monthly payment: ${formatCurrency(planSettings.extraBudget)}
Payments made this month: ${paidCount} of ${accounts.filter(a => a.paymentDate).length}
Principal paid in last 7 days: ${formatCurrency(weekPrincipal)}
Interest paid in last 7 days: ${formatCurrency(weekInterest)}
Payoff strategy: ${planSettings.strategy}

Important: Accounts marked "due today" are NOT missed or overdue — the payment is simply due today.
Write exactly 2 short sentences. First sentence: one key fact about their debt situation. Second sentence: one specific action they should take this week. Maximum 40 words total. Plain text only, no markdown.`;
}

function buildNudgePrompt(trigger) {
  const { type, data } = trigger;

  if (type === "all_paid") {
    return `You are Ellie, an AI debt coach in the ELDE app. The user paid ${formatCurrency(data.thisMonthPrincipal)} in principal this month with ${formatCurrency(data.totalDebt)} remaining.
Write 1-2 sentences: congratulate them briefly and give one specific next-month tip. Plain text only, no markdown.`;
  }

  if (type === "almost_done") {
    const a = data.account;
    return `You are Ellie, an AI debt coach in the ELDE app. The user's "${a.name}" has only ${data.months} month${data.months !== 1 ? "s" : ""} left at ${formatCurrency(a.balance)}.
Write 1-2 sentences: celebrate and tell them exactly what to do with the freed ${formatCurrency(a.minPayment)}/mo. Plain text only, no markdown.`;
  }

  if (type === "small_extra") {
    const a = data.focusAcct;
    return `You are Ellie, an AI debt coach in the ELDE app. User has ${formatCurrency(data.extraBudget)}/mo extra set. Highest-rate debt: "${a.name}" at ${a.rate}% APR, ${formatCurrency(a.balance)} remaining.
Write 1-2 sentences suggesting a specific slightly higher amount. Plain text only, no markdown.`;
  }

  if (type === "high_interest") {
    const a = data.account;
    return `You are Ellie, an AI debt coach in the ELDE app. User has "${a.name}" at ${a.rate}% APR, ${formatCurrency(a.balance)} remaining, no extra payment set.
Write 1-2 sentences on the cost of waiting and suggest a specific small extra payment. Plain text only, no markdown.`;
  }

  return null;
}

// ─── Insight Card Component ──────────────────────────────────────────────────

function InsightCard({ title, text, icon, onDismiss, loading, color = BLUE }) {
  return (
    <div style={{
      background: "#fff",
      border: `1.5px solid ${color}33`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 12,
      padding: "16px 18px",
      marginBottom: 16,
      position: "relative",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ fontSize: 22, flexShrink: 0 }}>{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            {title}
          </div>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 16, height: 16, border: `2px solid ${color}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <span style={{ fontSize: 13, color: "#888" }}>Ellie is thinking...</span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{text}</div>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} style={{ background: "none", border: "none", color: "#9CA3AF", fontSize: 16, cursor: "pointer", padding: 0, flexShrink: 0, lineHeight: 1 }}>✕</button>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Main Hook ───────────────────────────────────────────────────────────────

export function useAiInsights(accounts, allPayments, monthlyPayments, planSettings, isPro) {
  const [weeklyReport, setWeeklyReport] = useState(() => {
    try { return JSON.parse(localStorage.getItem(WEEKLY_REPORT_KEY)); } catch { return null; }
  });
  const [nudge, setNudge] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NUDGE_KEY + "_data")); } catch { return null; }
  });
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [weeklyDismissed, setWeeklyDismissed] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  const generateWeeklyReport = useCallback(async () => {
    if (!accounts.length || weeklyLoading) return;
    setWeeklyLoading(true);
    try {
      const prompt = buildWeeklyPrompt(accounts, allPayments, monthlyPayments, planSettings);
      const text = await callClaude(prompt);
      const report = { text, generatedAt: new Date().toISOString() };
      setWeeklyReport(report);
      setWeeklyDismissed(false);
      try { localStorage.setItem(WEEKLY_REPORT_KEY, JSON.stringify(report)); } catch {}
    } catch (e) {
      console.error("[AiInsights] Weekly report error:", e);
    } finally {
      setWeeklyLoading(false);
    }
  }, [accounts, allPayments, monthlyPayments, planSettings, weeklyLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateNudge = useCallback(async (trigger) => {
    if (nudgeLoading) return;
    setNudgeLoading(true);
    try {
      const prompt = buildNudgePrompt(trigger);
      if (!prompt) return;
      const text = await callClaude(prompt);
      const nudgeData = { text, type: trigger.type, generatedAt: new Date().toISOString() };
      setNudge(nudgeData);
      setNudgeDismissed(false);
      try {
        localStorage.setItem(NUDGE_KEY, new Date().toISOString());
        localStorage.setItem(NUDGE_KEY + "_data", JSON.stringify(nudgeData));
      } catch {}
    } catch (e) {
      console.error("[AiInsights] Nudge error:", e);
    } finally {
      setNudgeLoading(false);
    }
  }, [nudgeLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate first report after 24 hours, then refresh every 7 days
  useEffect(() => {
    if (!accounts.length) return;
    const lastGenerated = weeklyReport?.generatedAt;
    const accountsFirstAdded = localStorage.getItem("elde_accounts_first_added");
    if (!accountsFirstAdded) {
      try { localStorage.setItem("elde_accounts_first_added", new Date().toISOString()); } catch {}
      return;
    }
    const hoursSinceFirstAdded = (Date.now() - new Date(accountsFirstAdded).getTime()) / (1000 * 60 * 60);
    if (!lastGenerated && hoursSinceFirstAdded < 24) return; // Wait 24hrs for first report
    if (lastGenerated && daysSince(lastGenerated) < 7) return; // Then weekly
    generateWeeklyReport();
  }, [accounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stamp the report the first time it's actually on screen, so the 24h
  // "freshness" window starts when the user views it — not when it was generated.
  useEffect(() => {
    if (weeklyDismissed) return;
    if (!weeklyReport?.text || weeklyReport.viewedAt) return;
    const viewed = { ...weeklyReport, viewedAt: new Date().toISOString() };
    setWeeklyReport(viewed);
    try { localStorage.setItem(WEEKLY_REPORT_KEY, JSON.stringify(viewed)); } catch {}
  }, [weeklyReport, weeklyDismissed]);

  const weeklyExpired = !!weeklyReport?.viewedAt && hoursSince(weeklyReport.viewedAt) >= WEEKLY_VIEW_HOURS;

  // Check nudge triggers (max once per 24 hours)
  useEffect(() => {
    
    const lastNudge = localStorage.getItem(NUDGE_KEY);
    if (hoursSince(lastNudge) < NUDGE_COOLDOWN_HOURS) return;
    const trigger = detectNudgeTrigger(accounts, allPayments, monthlyPayments, planSettings);
    if (trigger) generateNudge(trigger);
  }, [accounts, monthlyPayments]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    weeklyReport,
    weeklyExpired,
    nudge,
    weeklyLoading,
    nudgeLoading,
    weeklyDismissed,
    nudgeDismissed,
    dismissWeekly: () => setWeeklyDismissed(true),
    dismissNudge: () => setNudgeDismissed(true),
  };
}

// ─── Dashboard Cards Component ───────────────────────────────────────────────

export function AiInsightCards({ accounts, allPayments, monthlyPayments, planSettings, isPro }) {
  const {
    weeklyReport, weeklyExpired, nudge,
    weeklyLoading, nudgeLoading,
    weeklyDismissed, nudgeDismissed,
    dismissWeekly, dismissNudge,
  } = useAiInsights(accounts, allPayments, monthlyPayments, planSettings, isPro);

  if (!accounts.length) return null;

  const showWeekly = !weeklyDismissed && !weeklyExpired && (weeklyLoading || weeklyReport?.text);
  const showNudge = !nudgeDismissed && (nudgeLoading || nudge?.text);

  if (!showWeekly && !showNudge) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      {showWeekly && (
        <InsightCard
          title="Weekly Debt Report"
          icon="📊"
          text={weeklyReport?.text}
          loading={weeklyLoading}
          onDismiss={dismissWeekly}
          color={BLUE}
        />
      )}
      {showNudge && (
        <InsightCard
          title="Ellie's Tip"
          icon="💬"
          text={nudge?.text}
          loading={nudgeLoading}
          onDismiss={dismissNudge}
          color={CYAN}
        />
      )}
    </div>
  );
}
